import i18n from "@/lib/i18n";
import router from "@/router";
import { showNotifyDialog, showOkCancelDialog } from "@/lib/dialog";
import { useThemeStore } from "@/stores/theme";
import { usePageEditStore } from "@/stores/pageEdit";
import type { Detail } from "@/lib/models/theme";
import { FEEDBACK_THEME_REQUIRED_FIELDS, THEME_FIELD_LABEL_KEYS } from "@/constants/themeReview";
import { logError } from "@/lib/utils/logger";

interface NovelLike {
  genre?: string;
  category?: string[];
}

/** 作品のジャンル・カテゴリが設定済みかどうか */
export function isNovelInfoFilled(novel: NovelLike | null | undefined): boolean {
  return !!novel && !!novel.genre && !!novel.category && novel.category.length > 0;
}

/** 未記入の必須項目の日本語ラベル一覧を返す（空なら全項目記入済み） */
export function getMissingRequiredFieldLabels(details: Detail[]): string[] {
  return FEEDBACK_THEME_REQUIRED_FIELDS.filter((name) => {
    const value = details.find((detail) => detail.name === name)?.value;
    return !value || !value.trim();
  }).map((name) => i18n.t(THEME_FIELD_LABEL_KEYS[name]) as string);
}

/**
 * 企画レビュー依頼前の 2 段階バリデーション（設計書 3.2）
 *
 * 1. 未保存の編集がある場合は保存を促して停止
 * 2. 作品のジャンル・カテゴリ未設定 → アラート + 作品情報編集ページへの誘導
 * 3. theme 必須項目（企画用あらすじ）の未記入 → アラート
 *
 * いずれも NG の場合はダイアログを表示して false を返す（確認画面へは遷移しない）。
 */
export function useThemeReviewValidation() {
  async function validate(options: { novelId: string; novel: NovelLike | null | undefined }): Promise<boolean> {
    const { novelId, novel } = options;

    // (0) 未保存の変更は判定対象にできないため、先に保存を促す（設計書 3.2 共通仕様 / 6.3）
    const pageEditStore = usePageEditStore();
    if (pageEditStore.isModified) {
      await showNotifyDialog({
        title: i18n.t("themeReview.validation.unsavedTitle") as string,
        content: i18n.t("themeReview.validation.unsavedMessage") as string,
      });
      return false;
    }

    // (1) 作品のジャンル・カテゴリ（theme 項目より先にチェック）
    if (!isNovelInfoFilled(novel)) {
      // theme ページではジャンル・カテゴリを編集できないため、作品情報編集ページへ誘導する
      const goToEdit = await showOkCancelDialog({
        title: i18n.t("themeReview.validation.novelInfoTitle") as string,
        content: i18n.t("themeReview.validation.novelInfoMessage") as string,
        okButton: i18n.t("themeReview.validation.novelInfoEditButton") as string,
        cancelButton: i18n.t("common.close") as string,
        okVariant: "primary",
      });
      if (goToEdit) {
        router.push({ name: "detail", params: { novelId } });
      }
      return false;
    }

    // (2) theme の必須項目（企画用あらすじ。保存済みデータで判定）
    const themeStore = useThemeStore();
    try {
      // パネルは theme ページ外の状態から開かれる可能性もあるため、対象作品のデータを保証する
      if (themeStore.theme?.novelId !== novelId) {
        await themeStore.fetchTheme(novelId);
      }
    } catch (error) {
      logError({ module: "useThemeReviewValidation", action: "validate" }, "theme データの取得に失敗", error);
      await showNotifyDialog({
        content: i18n.t("themeReview.validation.fetchErrorMessage") as string,
      });
      return false;
    }

    const missingLabels = getMissingRequiredFieldLabels(themeStore.theme?.detail ?? []);
    if (missingLabels.length > 0) {
      await showNotifyDialog({
        title: i18n.t("themeReview.validation.requiredFieldsTitle") as string,
        content: i18n.t("themeReview.validation.requiredFieldsMessage", {
          fields: missingLabels.map((label) => `「${label}」`).join(""),
        }) as string,
      });
      return false;
    }

    return true;
  }

  return { validate };
}
